import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Chip,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where, deleteDoc, doc, orderBy, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { clamp } from '../utils/numberGuard';
import {
  Employee,
  employmentTypeLabels,
  paymentTypeLabels,
  statusLabels,
  genderLabels,
  roleLabels,
} from '../types/employee';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Employees() {
  const { shop, user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);

  // ダイアログ状態
  const [openDialog, setOpenDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);

  // 画像関連
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // タブ状態
  const [tabValue, setTabValue] = useState(0);

  // フォームデータ
  const [formData, setFormData] = useState({
    // 基本情報
    employeeCode: '',
    email: '',
    password: '',
    passwordConfirm: '',
    role: 'staff' as NonNullable<Employee['role']>,

    // 個人情報
    lastName: '',
    firstName: '',
    lastNameKana: '',
    firstNameKana: '',
    birthDate: '',
    gender: 'not_specified' as Employee['personalInfo']['gender'],
    phone: '',

    // 緊急連絡先
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',

    // 住所
    postalCode: '',
    prefecture: '',
    city: '',
    street: '',
    building: '',

    // 雇用情報
    employmentType: 'owner_family' as Employee['employmentInfo']['employmentType'],
    position: '',
    department: '',
    hireDate: '',

    // 学生情報
    isStudent: false,
    schoolName: '',
    grade: '',
    graduationDate: '',

    // 給与情報
    paymentType: 'none' as Employee['employmentInfo']['paymentType'],
    hourlyWage: '',
    monthlyWage: '',
    dailyWage: '',
    transportationAllowanceType: 'per_day' as 'per_day' | 'monthly_fixed',
    transportationAllowance: '',
    nightShiftRate: '',

    // 予約システム連動
    isBookableStaff: true,
    showOnHomepage: true,
    displayName: '',
    description: '',
    nominationFee: '',

    // GPS勤怠
    gpsEnabled: true,
    gpsRadius: '100',

    // 週次シフトテンプレート
    weeklyTemplate: undefined as Employee['weeklyShiftTemplate'],

    // v3.0.0: 売上分配オーバーライド
    salesProfitDeductionRatePct: '',  // 0-100
    backRatePct: '',                  // 0-100
    loginEnabled: true,
  });

  // パスワード変更フォーム
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    newPasswordConfirm: '',
  });

  useEffect(() => {
    fetchEmployees();
  }, [shop]);

  const fetchEmployees = async () => {
    if (!shop) return;

    try {
      setLoading(true);
      const q = query(
        collection(db, 'employees'),
        where('shopId', '==', shop.id),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      // 削除済み（status: 'deleted'）を除外
      const data = querySnapshot.docs
        .filter(doc => doc.data().status !== 'deleted')
        .map((doc) => {
        const employeeData = doc.data();
        return {
          id: doc.id,
          ...employeeData,
          personalInfo: {
            ...employeeData.personalInfo,
            birthDate: employeeData.personalInfo?.birthDate?.toDate
              ? employeeData.personalInfo.birthDate.toDate()
              : new Date(),
          },
          employmentInfo: {
            ...employeeData.employmentInfo,
            hireDate: employeeData.employmentInfo?.hireDate?.toDate
              ? employeeData.employmentInfo.hireDate.toDate()
              : new Date(),
            graduationDate: employeeData.employmentInfo?.graduationDate?.toDate
              ? employeeData.employmentInfo.graduationDate.toDate()
              : undefined,
          },
          createdAt: employeeData.createdAt?.toDate
            ? employeeData.createdAt.toDate()
            : new Date(),
          updatedAt: employeeData.updatedAt?.toDate
            ? employeeData.updatedAt.toDate()
            : new Date(),
        };
      }) as Employee[];
      // displayOrderでソート（未設定は最後に）
      data.sort((a: any, b: any) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
      setEmployees(data);
      setOrderChanged(false);
    } catch (err) {
      console.error('従業員一覧の取得エラー:', err);
      setError('従業員一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // スタッフを上に移動
  const moveEmployeeUp = (index: number) => {
    if (index === 0) return;
    const newList = [...employees];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setEmployees(newList);
    setOrderChanged(true);
  };

  // スタッフを下に移動
  const moveEmployeeDown = (index: number) => {
    if (index === employees.length - 1) return;
    const newList = [...employees];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setEmployees(newList);
    setOrderChanged(true);
  };

  // 並び順を保存
  const handleSaveOrder = async () => {
    if (!shop || employees.length === 0) return;
    setSavingOrder(true);
    setError('');
    try {
      const batch = writeBatch(db);
      employees.forEach((emp, index) => {
        const empRef = doc(db, 'employees', emp.id);
        batch.update(empRef, { displayOrder: index });
      });
      await batch.commit();
      setSuccess('並び順を保存しました');
      setOrderChanged(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving order:', err);
      setError('並び順の保存に失敗しました');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleOpenDialog = (employee?: Employee) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        employeeCode: employee.employeeCode ?? '',
        email: employee.email ?? '',
        password: '',
        passwordConfirm: '',
        role: employee.role || 'staff',

        lastName: employee.personalInfo.lastName,
        firstName: employee.personalInfo.firstName,
        lastNameKana: employee.personalInfo.lastNameKana,
        firstNameKana: employee.personalInfo.firstNameKana,
        birthDate: employee.personalInfo.birthDate.toISOString().split('T')[0],
        gender: employee.personalInfo.gender,
        phone: employee.personalInfo.phone,

        emergencyContactName: employee.personalInfo.emergencyContact?.name || '',
        emergencyContactRelationship: employee.personalInfo.emergencyContact?.relationship || '',
        emergencyContactPhone: employee.personalInfo.emergencyContact?.phone || '',

        postalCode: employee.personalInfo.address?.postalCode || '',
        prefecture: employee.personalInfo.address?.prefecture || '',
        city: employee.personalInfo.address?.city || '',
        street: employee.personalInfo.address?.street || '',
        building: employee.personalInfo.address?.building || '',

        employmentType: employee.employmentInfo.employmentType,
        position: employee.employmentInfo.position,
        department: employee.employmentInfo.department || '',
        hireDate: employee.employmentInfo.hireDate?.toISOString().split('T')[0] || '',

        isStudent: employee.employmentInfo.isStudent || false,
        schoolName: employee.employmentInfo.schoolName || '',
        grade: employee.employmentInfo.grade || '',
        graduationDate: employee.employmentInfo.graduationDate
          ? employee.employmentInfo.graduationDate.toISOString().split('T')[0]
          : '',

        paymentType: employee.employmentInfo.paymentType,
        hourlyWage: employee.employmentInfo.hourlyWage?.toString() || '',
        monthlyWage: employee.employmentInfo.monthlyWage?.toString() || '',
        dailyWage: employee.employmentInfo.dailyWage?.toString() || '',
        transportationAllowanceType: employee.employmentInfo.transportationAllowanceType || 'per_day',
        transportationAllowance: employee.employmentInfo.transportationAllowance?.toString() || '',
        nightShiftRate: employee.employmentInfo.nightShiftRate?.toString() || '',

        isBookableStaff: employee.isBookableStaff ?? false,
        showOnHomepage: employee.showOnHomepage !== false,
        displayName: employee.displayName ?? employee.name ?? '',
        description: employee.description || '',
        nominationFee: employee.nominationFee?.toString() || '',

        salesProfitDeductionRatePct:
          employee.salesProfitDeductionRate != null
            ? (employee.salesProfitDeductionRate * 100).toString()
            : '',
        backRatePct:
          employee.backRate != null ? (employee.backRate * 100).toString() : '',
        loginEnabled: employee.loginEnabled !== false,

        gpsEnabled: employee.gpsAttendance?.enabled || false,
        gpsRadius: employee.gpsAttendance?.allowedRadius?.toString() || '100',

        weeklyTemplate: employee.weeklyShiftTemplate,
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        employeeCode: '',
        email: '',
        password: '',
        passwordConfirm: '',
        role: 'staff',
        lastName: '',
        firstName: '',
        lastNameKana: '',
        firstNameKana: '',
        birthDate: '',
        gender: 'not_specified',
        phone: '',
        emergencyContactName: '',
        emergencyContactRelationship: '',
        emergencyContactPhone: '',
        postalCode: '',
        prefecture: '',
        city: '',
        street: '',
        building: '',
        employmentType: 'owner_family',
        position: '',
        department: '',
        hireDate: '',
        isStudent: false,
        schoolName: '',
        grade: '',
        graduationDate: '',
        paymentType: 'none',
        hourlyWage: '',
        monthlyWage: '',
        dailyWage: '',
        transportationAllowanceType: 'per_day',
        transportationAllowance: '',
        nightShiftRate: '',
        isBookableStaff: true,
        showOnHomepage: true,
        displayName: '',
        description: '',
        nominationFee: '',
        gpsEnabled: true,
        gpsRadius: '100',

        weeklyTemplate: undefined,

        salesProfitDeductionRatePct: '',
        backRatePct: '',
        loginEnabled: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEmployee(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setError('');
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!shop) return;

    setError('');
    setSuccess('');

    // バリデーション
    if (!formData.employeeCode || !formData.email) {
      setError('従業員番号とメールアドレスは必須です');
      return;
    }

    if (!editingEmployee && !formData.password) {
      setError('パスワードは必須です');
      return;
    }

    if (!editingEmployee && formData.password !== formData.passwordConfirm) {
      setError('パスワードが一致しません');
      return;
    }

    if (!formData.lastName || !formData.firstName) {
      setError('氏名は必須です');
      return;
    }

    // 生年月日、役職、入社日はオプショナルに変更（後で入力可能）

    // 金額と料率に負の値が入らないようにする。
    // 入力欄の min はブラウザのヒントで、貼り付けも保存も止めない。
    // 負の時給・負のバック率は、そのまま給与計算に入って支給額を狂わせる。
    for (const [key, label] of [
      ['hourlyWage', '時給'], ['monthlyWage', '月給'], ['dailyWage', '日給'],
      ['nominationFee', '指名料'], ['nightShiftRate', '深夜割増'],
      ['backRatePct', 'バック率'], ['salesProfitDeductionRatePct', '利益控除率'],
    ] as const) {
      const raw = (formData as any)[key];
      if (raw === '' || raw === undefined || raw === null) continue;
      const n = parseFloat(String(raw));
      if (!Number.isFinite(n) || n < 0) {
        setError(`${label}は0以上の数を入れてください`);
        return;
      }
    }
    // 率は 0〜100%
    for (const [key, label] of [
      ['backRatePct', 'バック率'], ['salesProfitDeductionRatePct', '利益控除率'],
    ] as const) {
      const raw = (formData as any)[key];
      if (raw === '' || raw === undefined || raw === null) continue;
      if (parseFloat(String(raw)) > 100) {
        setError(`${label}は100%以下にしてください`);
        return;
      }
    }

    try {
      // Upload photo if a new file was selected
      let profileImageUrl = editingEmployee?.profileImage || '';
      if (photoFile) {
        const storageRef = ref(storage, `employees/${shop.id}/${Date.now()}_${photoFile.name}`);
        await uploadBytes(storageRef, photoFile);
        profileImageUrl = await getDownloadURL(storageRef);
      }

      if (editingEmployee) {
        // 更新
        const updateEmployee = httpsCallable(functions, 'updateEmployee');
        await updateEmployee({
          employeeId: editingEmployee.id,
          email: formData.email,
          employeeCode: formData.employeeCode,
          role: formData.role,
          personalInfo: {
            lastName: formData.lastName,
            firstName: formData.firstName,
            lastNameKana: formData.lastNameKana,
            firstNameKana: formData.firstNameKana,
            birthDate: formData.birthDate,
            gender: formData.gender,
            phone: formData.phone,
            emergencyContact: formData.emergencyContactName
              ? {
                  name: formData.emergencyContactName,
                  relationship: formData.emergencyContactRelationship,
                  phone: formData.emergencyContactPhone,
                }
              : undefined,
            address: formData.postalCode
              ? {
                  postalCode: formData.postalCode,
                  prefecture: formData.prefecture,
                  city: formData.city,
                  street: formData.street,
                  building: formData.building,
                }
              : undefined,
          },
          ...(formData.employmentType || formData.position || formData.hireDate || formData.paymentType
            ? {
                employmentInfo: {
                  employmentType: formData.employmentType,
                  position: formData.position,
                  department: formData.department,
                  hireDate: formData.hireDate,
                  isStudent: formData.isStudent,
                  schoolName: formData.schoolName,
                  grade: formData.grade,
                  graduationDate: formData.graduationDate,
                  paymentType: formData.paymentType,
                  hourlyWage: formData.hourlyWage ? parseFloat(formData.hourlyWage) : undefined,
                  monthlyWage: formData.monthlyWage ? parseFloat(formData.monthlyWage) : undefined,
                  dailyWage: formData.dailyWage ? parseFloat(formData.dailyWage) : undefined,
                  transportationAllowanceType: formData.transportationAllowanceType,
                  transportationAllowance: formData.transportationAllowance
                    ? parseFloat(formData.transportationAllowance)
                    : undefined,
                  nightShiftRate: formData.nightShiftRate ? parseFloat(formData.nightShiftRate) : undefined,
                },
              }
            : {}),
          isBookableStaff: formData.isBookableStaff,
          showOnHomepage: formData.showOnHomepage,
          displayName: formData.displayName,
          profileImage: profileImageUrl,
          description: formData.description,
          nominationFee: formData.nominationFee ? parseFloat(formData.nominationFee) : 0,
          gpsAttendance: {
            enabled: formData.gpsEnabled,
            allowedRadius: parseFloat(formData.gpsRadius),
          },
          weeklyShiftTemplate: formData.weeklyTemplate,
          // v3.0.0: 売上分配オーバーライド + ログイン制御
          salesProfitDeductionRate: formData.salesProfitDeductionRatePct
            ? parseFloat(formData.salesProfitDeductionRatePct) / 100
            : null,
          backRate: formData.backRatePct ? parseFloat(formData.backRatePct) / 100 : null,
          loginEnabled: formData.loginEnabled,
        });

        setSuccess('従業員情報を更新しました');
      } else {
        // 新規作成
        const createEmployee = httpsCallable(functions, 'createEmployee');
        await createEmployee({
          shopId: shop.id,
          email: formData.email,
          password: formData.password,
          employeeCode: formData.employeeCode,
          role: formData.role,
          personalInfo: {
            lastName: formData.lastName,
            firstName: formData.firstName,
            lastNameKana: formData.lastNameKana,
            firstNameKana: formData.firstNameKana,
            birthDate: formData.birthDate,
            gender: formData.gender,
            phone: formData.phone,
            emergencyContact: formData.emergencyContactName
              ? {
                  name: formData.emergencyContactName,
                  relationship: formData.emergencyContactRelationship,
                  phone: formData.emergencyContactPhone,
                }
              : undefined,
            address: formData.postalCode
              ? {
                  postalCode: formData.postalCode,
                  prefecture: formData.prefecture,
                  city: formData.city,
                  street: formData.street,
                  building: formData.building,
                }
              : undefined,
          },
          ...(formData.employmentType || formData.position || formData.hireDate || formData.paymentType
            ? {
                employmentInfo: {
                  employmentType: formData.employmentType,
                  position: formData.position,
                  department: formData.department,
                  hireDate: formData.hireDate,
                  isStudent: formData.isStudent,
                  schoolName: formData.schoolName,
                  grade: formData.grade,
                  graduationDate: formData.graduationDate,
                  paymentType: formData.paymentType,
                  hourlyWage: formData.hourlyWage ? parseFloat(formData.hourlyWage) : undefined,
                  monthlyWage: formData.monthlyWage ? parseFloat(formData.monthlyWage) : undefined,
                  dailyWage: formData.dailyWage ? parseFloat(formData.dailyWage) : undefined,
                  transportationAllowanceType: formData.transportationAllowanceType,
                  transportationAllowance: formData.transportationAllowance
                    ? parseFloat(formData.transportationAllowance)
                    : undefined,
                  nightShiftRate: formData.nightShiftRate ? parseFloat(formData.nightShiftRate) : undefined,
                },
              }
            : {}),
          isBookableStaff: formData.isBookableStaff,
          showOnHomepage: formData.showOnHomepage,
          displayName: formData.displayName || `${formData.lastName} ${formData.firstName}`,
          profileImage: profileImageUrl,
          description: formData.description,
          nominationFee: formData.nominationFee ? parseFloat(formData.nominationFee) : 0,
          gpsAttendance: {
            enabled: formData.gpsEnabled,
            allowedRadius: parseFloat(formData.gpsRadius),
          },
          weeklyShiftTemplate: formData.weeklyTemplate,
          // v3.0.0: 売上分配オーバーライド + ログイン制御
          salesProfitDeductionRate: formData.salesProfitDeductionRatePct
            ? parseFloat(formData.salesProfitDeductionRatePct) / 100
            : null,
          backRate: formData.backRatePct ? parseFloat(formData.backRatePct) / 100 : null,
          loginEnabled: formData.loginEnabled,
        });

        setSuccess('従業員を作成しました');
      }

      handleCloseDialog();
      await fetchEmployees();
    } catch (err: any) {
      console.error('従業員の保存エラー:', err);
      setError(err.message || '従業員の保存に失敗しました');
    }
  };

  const handleOpenPasswordDialog = (employee: Employee) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setEditingEmployee(employee);
    setPasswordData({ newPassword: '', newPasswordConfirm: '' });
    setOpenPasswordDialog(true);
  };

  const handleChangePassword = async () => {
    if (!editingEmployee) return;

    setError('');

    if (!passwordData.newPassword) {
      setError('新しいパスワードを入力してください');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }

    if (passwordData.newPassword !== passwordData.newPasswordConfirm) {
      setError('パスワードが一致しません');
      return;
    }

    try {
      const changePassword = httpsCallable(functions, 'changeEmployeePassword');
      await changePassword({
        employeeId: editingEmployee.id,
        newPassword: passwordData.newPassword,
      });

      setSuccess('パスワードを変更しました');
      setOpenPasswordDialog(false);
      setEditingEmployee(null);
    } catch (err: any) {
      console.error('パスワード変更エラー:', err);
      setError(err.message || 'パスワードの変更に失敗しました');
    }
  };

  const handleOpenDeleteDialog = (employee: Employee) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setDeleteTarget(employee);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget || !user) return;

    try {
      // 論理削除: statusをdeletedに変更
      await updateDoc(doc(db, 'employees', deleteTarget.id), {
        status: 'deleted',
        deletedAt: serverTimestamp(),
        deletedBy: user.uid,
        updatedAt: serverTimestamp(),
      });
      setSuccess('従業員を削除しました');
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      await fetchEmployees();
    } catch (err) {
      console.error('削除エラー:', err);
      setError('削除に失敗しました');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">スタッフ管理</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {orderChanged && (
            <Button
              variant="contained"
              color="warning"
              startIcon={savingOrder ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveOrder}
              disabled={savingOrder}
            >
              {savingOrder ? '保存中...' : '並び順を保存'}
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            スタッフ追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={80} align="center">並び順</TableCell>
              <TableCell>従業員番号</TableCell>
              <TableCell>氏名</TableCell>
              <TableCell>ロール</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>役職</TableCell>
              <TableCell>雇用形態</TableCell>
              <TableCell>給与</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee, index) => (
              <TableRow key={employee.id}>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Tooltip title="上に移動">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => moveEmployeeUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="下に移動">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => moveEmployeeDown(index)}
                          disabled={index === employees.length - 1}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>{employee.employeeCode}</TableCell>
                <TableCell>
                  {employee.personalInfo.lastName} {employee.personalInfo.firstName}
                  {employee.isBookableStaff && (
                    <Chip label="予約可" size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={roleLabels[employee.role || 'staff']}
                    color={employee.role === 'owner' ? 'error' : employee.role === 'manager' ? 'warning' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{employee.email}</TableCell>
                <TableCell>{employee.employmentInfo.position}</TableCell>
                <TableCell>{employmentTypeLabels[employee.employmentInfo.employmentType]}</TableCell>
                <TableCell>
                  {employee.employmentInfo.paymentType === 'hourly' && employee.employmentInfo.hourlyWage
                    ? `時給${employee.employmentInfo.hourlyWage.toLocaleString()}円`
                    : employee.employmentInfo.paymentType === 'monthly' && employee.employmentInfo.monthlyWage
                    ? `月給${employee.employmentInfo.monthlyWage.toLocaleString()}円`
                    : employee.employmentInfo.paymentType === 'daily' && employee.employmentInfo.dailyWage
                    ? `日給${employee.employmentInfo.dailyWage.toLocaleString()}円`
                    : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusLabels[employee.status ?? 'active']}
                    color={(employee.status ?? 'active') === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(employee)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleOpenPasswordDialog(employee)}>
                    <KeyIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleOpenDeleteDialog(employee)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  スタッフが登録されていません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* スタッフ追加・編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingEmployee ? 'スタッフ編集' : 'スタッフ追加'}</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="基本情報" />
            <Tab label="個人情報" />
            <Tab label="雇用・給与情報" />
            <Tab label="その他" />
            <Tab label="定期シフトパターン" />
          </Tabs>

          {/* タブ1: 基本情報 */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="従業員番号"
                  value={formData.employeeCode}
                  onChange={(e) => setFormData({ ...formData, employeeCode: e.target.value })}
                  required
                  helperText="例: EMP001"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="メールアドレス"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  helperText={editingEmployee ? 'メールアドレスを変更すると、次回ログイン時に新しいアドレスが必要になります' : ''}
                />
              </Grid>
              {!editingEmployee && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="初期パスワード"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      helperText="6文字以上"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="パスワード確認"
                      type="password"
                      value={formData.passwordConfirm}
                      onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                      required
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="ロール（権限）"
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value as NonNullable<Employee['role']> })
                  }
                  helperText="オーナー: 全権限 / マネージャー: 管理権限 / スタッフ: 基本操作のみ"
                >
                  {Object.entries(roleLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="姓"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="名"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="セイ"
                  value={formData.lastNameKana}
                  onChange={(e) => setFormData({ ...formData, lastNameKana: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="メイ"
                  value={formData.firstNameKana}
                  onChange={(e) => setFormData({ ...formData, firstNameKana: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="生年月日"
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="任意"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="性別"
                  value={formData.gender}
                  onChange={(e) =>
                    setFormData({ ...formData, gender: e.target.value as typeof formData.gender })
                  }
                >
                  {Object.entries(genderLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="電話番号"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  helperText="例: 090-1234-5678"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* タブ2: 個人情報 */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              緊急連絡先
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="名前"
                  value={formData.emergencyContactName}
                  onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="続柄"
                  value={formData.emergencyContactRelationship}
                  onChange={(e) =>
                    setFormData({ ...formData, emergencyContactRelationship: e.target.value })
                  }
                  helperText="例: 父、母、配偶者"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="電話番号"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              住所
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="郵便番号"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  helperText="例: 123-4567"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="都道府県"
                  value={formData.prefecture}
                  onChange={(e) => setFormData({ ...formData, prefecture: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="市区町村"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="番地"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="建物名・部屋番号"
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* タブ3: 雇用・給与情報 */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="雇用形態"
                  value={formData.employmentType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      employmentType: e.target.value as typeof formData.employmentType,
                    })
                  }
                  helperText="家族経営等の場合は未設定でもOK"
                >
                  {Object.entries(employmentTypeLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="役職・ポジション"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  helperText="例: スタイリスト、アシスタント（任意）"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="部署"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="入社日"
                  type="date"
                  value={formData.hireDate}
                  onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="任意"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      id="is-student"
                      checked={formData.isStudent}
                      onChange={(e) => setFormData({ ...formData, isStudent: e.target.checked })}
                    />
                  }
                  label="学生"
                />
              </Grid>

              {formData.isStudent && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="学校名"
                      value={formData.schoolName}
                      onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="学年"
                      value={formData.grade}
                      onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                      helperText="例: 大学2年"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="卒業予定日"
                      type="date"
                      value={formData.graduationDate}
                      onChange={(e) => setFormData({ ...formData, graduationDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  給与情報
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="給与タイプ"
                  value={formData.paymentType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      paymentType: e.target.value as typeof formData.paymentType,
                    })
                  }
                  helperText="オーナー等の場合は未設定でもOK"
                >
                  {Object.entries(paymentTypeLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {formData.paymentType === 'hourly' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="時給"
                      type="number"
                      value={formData.hourlyWage}
                      onChange={(e) => setFormData({ ...formData, hourlyWage: e.target.value })}
                      InputProps={{ endAdornment: '円' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="深夜割増率"
                      type="number"
                      value={formData.nightShiftRate}
                      onChange={(e) => setFormData({ ...formData, nightShiftRate: e.target.value })}
                      helperText="例: 1.25 = 25%増し"
                      inputProps={{ step: '0.01', min: '1.0' }}
                    />
                  </Grid>
                </>
              )}

              {formData.paymentType === 'monthly' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="月給"
                    type="number"
                    value={formData.monthlyWage}
                    onChange={(e) => setFormData({ ...formData, monthlyWage: e.target.value })}
                    InputProps={{ endAdornment: '円' }}
                  />
                </Grid>
              )}

              {formData.paymentType === 'daily' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="日給"
                    type="number"
                    value={formData.dailyWage}
                    onChange={(e) => setFormData({ ...formData, dailyWage: e.target.value })}
                    InputProps={{ endAdornment: '円' }}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="交通費タイプ"
                  value={formData.transportationAllowanceType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      transportationAllowanceType: e.target.value as 'per_day' | 'monthly_fixed',
                    })
                  }
                >
                  <MenuItem value="per_day">1回出勤あたり</MenuItem>
                  <MenuItem value="monthly_fixed">月額固定</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="交通費"
                  type="number"
                  value={formData.transportationAllowance}
                  onChange={(e) => setFormData({ ...formData, transportationAllowance: e.target.value })}
                  InputProps={{ endAdornment: '円' }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* タブ4: その他 */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  予約システム連動
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      id="is-bookable-staff"
                      checked={formData.isBookableStaff}
                      onChange={(e) => setFormData({ ...formData, isBookableStaff: e.target.checked })}
                    />
                  }
                  label="予約で指名可能にする"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      id="show-on-homepage"
                      checked={formData.showOnHomepage}
                      onChange={(e) => setFormData({ ...formData, showOnHomepage: e.target.checked })}
                    />
                  }
                  label="ホームページに表示する"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="表示名"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  helperText="ホームページ・予約画面で表示される名前"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="紹介文"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  helperText="ホームページ・予約画面で表示される紹介文"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="指名料"
                  type="number"
                  value={formData.nominationFee}
                  onChange={(e) => setFormData({ ...formData, nominationFee: e.target.value })}
                  helperText="スタッフ指名時の追加料金（円）"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>¥</Typography>,
                  }}
                />
              </Grid>

              {/* v3.0.0: 売上分配・バック設定 + ログイン制御 */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'warning.main', borderRadius: 1, bgcolor: 'warning.50' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    売上分配・バック設定（個別オーバーライド）
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    空欄ならカテゴリ既定値・店舗既定値を使用します。
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="利益控除率 (%)"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        value={formData.salesProfitDeductionRatePct}
                        onChange={(e) => setFormData({ ...formData, salesProfitDeductionRatePct: e.target.value })}
                        helperText="例: 10 → 取り分0.9 / 20 → 取り分0.8"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="個別バック率 (%)"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        value={formData.backRatePct}
                        onChange={(e) => setFormData({ ...formData, backRatePct: e.target.value })}
                        helperText="例: 50 → 粗利の50%"
                      />
                    </Grid>
                  </Grid>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.loginEnabled}
                        onChange={(e) => setFormData({ ...formData, loginEnabled: e.target.checked })}
                      />
                    }
                    label="ログインを許可（OFFで停止、退職者用）"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    プロフィール画像
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<PhotoCameraIcon />}
                  >
                    画像を選択
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </Button>
                  {(photoPreview || editingEmployee?.profileImage) && (
                    <Box sx={{ mt: 2 }}>
                      <img
                        src={photoPreview || editingEmployee?.profileImage}
                        alt="プロフィール"
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  GPS勤怠設定
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      id="gps-enabled"
                      checked={formData.gpsEnabled}
                      onChange={(e) => setFormData({ ...formData, gpsEnabled: e.target.checked })}
                    />
                  }
                  label="GPS打刻を使用する"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="許可範囲"
                  type="number"
                  value={formData.gpsRadius}
                  onChange={(e) => setFormData({ ...formData, gpsRadius: e.target.value })}
                  InputProps={{ endAdornment: 'メートル' }}
                  disabled={!formData.gpsEnabled}
                  helperText="店舗からこの範囲内でのみ打刻可能"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* タブ5: 定期シフトパターン */}
          <TabPanel value={tabValue} index={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  週次シフトテンプレート
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  各曜日の定期勤務時間を設定します。シフト作成時に自動的に適用されます。
                </Typography>
              </Grid>

              {/* 全曜日を統一したコンポーネントで表示 */}
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => {
                const dayLabels = ['月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日'];
                const dayKey = day as 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

                return (
                  <Grid item xs={12} key={day}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            id={`template-${day}`}
                            checked={formData.weeklyTemplate?.[dayKey]?.enabled || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                weeklyTemplate: {
                                  ...formData.weeklyTemplate,
                                  [dayKey]: {
                                    ...(formData.weeklyTemplate?.[dayKey] || { startTime: '09:00', endTime: '18:00', breakMinutes: 60 }),
                                    enabled: e.target.checked,
                                  },
                                },
                              })
                            }
                          />
                        }
                        label={dayLabels[index]}
                        sx={{ width: 120 }}
                      />
                      <TextField
                        label="開始時刻"
                        type="time"
                        value={formData.weeklyTemplate?.[dayKey]?.startTime || '09:00'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            weeklyTemplate: {
                              ...formData.weeklyTemplate,
                              [dayKey]: {
                                ...(formData.weeklyTemplate?.[dayKey] || { enabled: false, breakMinutes: 60 }),
                                startTime: e.target.value,
                                endTime: formData.weeklyTemplate?.[dayKey]?.endTime || '18:00',
                              },
                            },
                          })
                        }
                        disabled={!formData.weeklyTemplate?.[dayKey]?.enabled}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 130 }}
                        size="small"
                      />
                      <Typography>〜</Typography>
                      <TextField
                        label="終了時刻"
                        type="time"
                        value={formData.weeklyTemplate?.[dayKey]?.endTime || '18:00'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            weeklyTemplate: {
                              ...formData.weeklyTemplate,
                              [dayKey]: {
                                ...(formData.weeklyTemplate?.[dayKey] || { enabled: false, breakMinutes: 60 }),
                                startTime: formData.weeklyTemplate?.[dayKey]?.startTime || '09:00',
                                endTime: e.target.value,
                              },
                            },
                          })
                        }
                        disabled={!formData.weeklyTemplate?.[dayKey]?.enabled}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 130 }}
                        size="small"
                      />
                      <TextField
                        label="休憩"
                        type="number"
                        value={formData.weeklyTemplate?.[dayKey]?.breakMinutes ?? 60}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            weeklyTemplate: {
                              ...formData.weeklyTemplate,
                              [dayKey]: {
                                ...(formData.weeklyTemplate?.[dayKey] || { enabled: false, startTime: '09:00', endTime: '18:00' }),
                                // 負の休憩時間は労働時間を増やし、給与が過大になる。
                        breakMinutes: clamp(e.target.value, 0, 24 * 60),
                              },
                            },
                          })
                        }
                        disabled={!formData.weeklyTemplate?.[dayKey]?.enabled}
                        InputProps={{ endAdornment: <Typography variant="body2">分</Typography> }}
                        sx={{ width: 100 }}
                        size="small"
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingEmployee ? '更新' : '作成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* パスワード変更ダイアログ */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
        <DialogTitle>パスワード変更</DialogTitle>
        <DialogContent>
          {editingEmployee && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {editingEmployee.personalInfo.lastName} {editingEmployee.personalInfo.firstName} (
              {editingEmployee.email})
            </Typography>
          )}
          <TextField
            fullWidth
            label="新しいパスワード"
            type="password"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
            sx={{ mb: 2 }}
            helperText="6文字以上"
          />
          <TextField
            fullWidth
            label="パスワード確認"
            type="password"
            value={passwordData.newPasswordConfirm}
            onChange={(e) => setPasswordData({ ...passwordData, newPasswordConfirm: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>キャンセル</Button>
          <Button onClick={handleChangePassword} variant="contained">
            変更
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>スタッフ削除</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Typography>
              {deleteTarget.personalInfo.lastName} {deleteTarget.personalInfo.firstName} (
              {deleteTarget.employeeCode}) を削除してもよろしいですか？
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
